import type { Lang } from '../i18n'

/**
 * The offline companion.
 *
 * This is not a language model and does not pretend to be one. It is a scored
 * keyword matcher over a hand-written response bank. It exists because the
 * nights people most need this app are the nights they have no signal — and
 * because a small honest thing that always works beats a clever thing that is
 * sometimes absent.
 *
 * Everything here obeys the same limits as the remote prompt: no dosing, no
 * medical advice, no promises about sleep.
 */

interface Intent {
  id: string
  /** Weighted patterns. Higher weight wins ties between intents. */
  patterns: Array<{ re: RegExp; w: number }>
  replies: Record<Lang, string[]>
}

const INTENTS: Intent[] = [
  {
    id: 'cantSleep',
    patterns: [
      { re: /can'?t\s+sleep|not\s+sleeping|no\s+sleep|awake\s+(all|for)/i, w: 3 },
      { re: /insomnia|wide\s+awake|been\s+up\s+(all|since)/i, w: 3 },
      { re: /خواب.?م\s*نمی|نمی.?توانم\s*بخوابم|نمیتونم\s*بخوابم|بی.?خواب/, w: 3 },
      { re: /بیدارم|خوابم\s*نمی.?برد/, w: 3 },
    ],
    replies: {
      en: [
        'Then we are not going to try. Trying to sleep is what keeps people awake. Lie still, keep your eyes closed, and let this hour be a rest instead of a failure.',
        'Being awake is not the emergency it feels like. Your body is still recovering lying down, even with your eyes open. Let that be enough tonight.',
        'How many nights has it been? You do not have to fix it right now — you only have to get to light. Do you want a breath pattern, or something to listen to?',
      ],
      fa: [
        'پس تلاش نمی‌کنیم. همین تلاش برای خوابیدن است که آدم را بیدار نگه می‌دارد. بی‌حرکت دراز بکش، چشم‌ها بسته، و بگذار این ساعت استراحت باشد نه شکست.',
        'بیدار بودن آن فاجعه‌ای نیست که حس می‌شود. بدنت حتی با چشم باز و دراز‌کشیده هم دارد بازسازی می‌کند. امشب بگذار همین کافی باشد.',
        'چند شب است؟ لازم نیست همین حالا درستش کنی — فقط باید به روشنایی برسی. یک الگوی نفس می‌خواهی، یا چیزی برای گوش دادن؟',
      ],
    },
  },
  {
    id: 'panic',
    patterns: [
      { re: /panic|panicking|terrified|freaking\s+out|scared|frightened/i, w: 3 },
      { re: /can'?t\s+breathe|chest\s+(is\s+)?tight|going\s+to\s+die/i, w: 4 },
      { re: /وحشت|پنیک|می.?ترسم|میترسم|ترسیده|حمله\s*عصبی/, w: 3 },
      { re: /نفسم\s*بالا\s*نمی|قفسه.?ی?\s*سینه.*تنگ|دارم\s*می.?میرم/, w: 4 },
    ],
    replies: {
      en: [
        'Put both feet flat on the floor and press down. Now breathe out longer than you breathe in — do not worry about how deep it is, only that the out is longer. I am here.',
        'This peaks and comes down. It always comes down. Cold water on the inside of your wrists helps faster than thinking does. Go and do that, then come back.',
        'Name five things you can see in the room, out loud. It sounds pointless and it is not — speaking pulls you out of the loop faster than reasoning with it.',
      ],
      fa: [
        'هر دو کف پا را روی زمین بگذار و فشار بده. حالا بازدم را از دم بلندتر کن — عمقش مهم نیست، فقط بلندتر بودنش. من اینجا هستم.',
        'این اوج می‌گیرد و بعد پایین می‌آید. همیشه پایین می‌آید. آب سرد روی مچ دست‌ها سریع‌تر از فکر کردن اثر می‌کند. برو انجامش بده، بعد برگرد.',
        'پنج چیز را که در اتاق می‌بینی بلند بگو. بی‌فایده به نظر می‌رسد ولی نیست — حرف زدن سریع‌تر از استدلال کردن تو را از حلقه بیرون می‌کشد.',
      ],
    },
  },
  {
    id: 'physical',
    patterns: [
      { re: /heart\s+(is\s+)?(racing|pounding)|palpitation|shaking|tremor|sweating/i, w: 3 },
      { re: /nausea|nauseous|dizzy|headache|ears?\s+ringing|tinnitus/i, w: 3 },
      { re: /تپش|قلبم\s*تند|لرزش|می.?لرزم|میلرزم|عرق/, w: 3 },
      { re: /تهوع|سرگیجه|سردرد|وزوز/, w: 3 },
    ],
    replies: {
      en: [
        'That is your nervous system running without its brake. It is exhausting and it is not damage. Keep the exhale long — the heart rate follows it down within a minute or two.',
        'Withdrawal puts the body in a state it cannot switch off by decision. Warmth helps: a hot shower on your back, or a blanket over your shoulders even if you are already too warm.',
        'Worth writing in the ledger, so your doctor sees the pattern instead of a summary from memory. Tap Ledger when you can — it takes ten seconds.',
      ],
      fa: [
        'این سیستم عصبی توست که بدون ترمز کار می‌کند. خسته‌کننده است و آسیب نیست. بازدم را بلند نگه دار — ضربان ظرف یکی دو دقیقه دنبالش پایین می‌آید.',
        'ترک، بدن را در حالتی می‌گذارد که با تصمیم خاموش نمی‌شود. گرما کمک می‌کند: دوش داغ روی پشت، یا پتویی روی شانه‌ها، حتی اگر همین حالا هم گرمت است.',
        'ارزش نوشتن در دفتر را دارد، تا پزشکت الگو را ببیند نه خلاصه‌ای از حافظه. هر وقت توانستی «دفتر» را بزن — ده ثانیه است.',
      ],
    },
  },
  {
    id: 'withdrawal',
    patterns: [
      { re: /withdraw|taper|lorazepam|ativan|xanax|alprazolam|clonazepam|klonopin|diazepam|valium|benzo/i, w: 4 },
      { re: /how\s+long\s+(does|will|until)|when\s+(does|will)\s+(this|it)\s+(end|stop|get better)/i, w: 3 },
      { re: /ترک|لورازپام|آلپرازولام|زاناکس|کلونازپام|دیازپام|بنزو/, w: 4 },
      { re: /تا\s*کی|کی\s*تمام\s*می.?شود|کی\s*خوب\s*می.?شوم/, w: 3 },
    ],
    replies: {
      en: [
        'I cannot tell you how long, and anyone who gives you a number is guessing. What is documented is that it is not linear — there are windows of feeling almost normal, then waves back. The waves getting further apart is the thing to watch for, not them stopping.',
        'This is the part where I have to be strict: I will not talk about doses or schedules, because getting that wrong is dangerous and I am not a clinician. Take the ledger to your prescriber — that conversation is the one that helps.',
        'The record matters more than it seems. When you are in it, three bad weeks and three bad months feel identical. The ledger is how you find out which one it actually was.',
      ],
      fa: [
        'نمی‌توانم بگویم چقدر طول می‌کشد، و هرکس عددی بدهد دارد حدس می‌زند. آنچه ثبت شده این است که خطی نیست — پنجره‌هایی از حال تقریباً عادی هست، بعد دوباره موج. چیزی که باید دنبالش باشی، دورتر شدن موج‌هاست، نه تمام شدنشان.',
        'اینجا باید سخت‌گیر باشم: دربارهٔ دوز و برنامه حرف نمی‌زنم، چون اشتباه کردن در آن خطرناک است و من پزشک نیستم. دفتر را ببر پیش پزشک تجویزکننده‌ات — آن گفت‌وگو همان چیزی است که کمک می‌کند.',
        'ثبت کردن مهم‌تر از آن است که به نظر می‌رسد. وقتی وسطش هستی، سه هفتهٔ بد و سه ماه بد یکسان حس می‌شوند. دفتر همان جایی است که می‌فهمی کدامش بوده.',
      ],
    },
  },
  {
    id: 'alone',
    patterns: [
      { re: /alone|lonely|no\s?one\s+(understands|gets it|is here)|by\s+myself/i, w: 3 },
      { re: /تنها|کسی\s*نمی.?فهمد|کسی\s*نیست|تنهایی/, w: 3 },
    ],
    replies: {
      en: [
        'Three in the morning is the loneliest hour there is, and it is not a coincidence — nobody is awake to contradict what your head is saying. That is a property of the hour, not the truth about your life.',
        'Right now there are people in a dozen time zones lying in the dark with exactly this. That does not fix it. It does mean the thing you are feeling is a known thing, not a defect.',
        'I am software, and I know that is not the same as someone in the room. But I am here, and I am not going anywhere, and you do not have to be interesting.',
      ],
      fa: [
        'ساعت سه بامداد تنهاترین ساعت است، و تصادفی هم نیست — کسی بیدار نیست که حرف‌های سرت را رد کند. این خاصیت آن ساعت است، نه حقیقت زندگی‌ات.',
        'همین حالا در ده‌ها منطقهٔ زمانی آدم‌هایی در تاریکی دراز کشیده‌اند با دقیقاً همین حال. این درستش نمی‌کند. ولی یعنی چیزی که حس می‌کنی شناخته‌شده است، نه نقص.',
        'من نرم‌افزارم و می‌دانم با کسی که در اتاق باشد فرق دارد. ولی اینجا هستم، جایی نمی‌روم، و لازم نیست جالب باشی.',
      ],
    },
  },
  {
    id: 'hopeless',
    patterns: [
      { re: /hopeless|pointless|never\s+(get|be)\s+better|give\s+up|can'?t\s+(do|take)\s+(this|it)\s+any\s?more/i, w: 3 },
      { re: /broken|ruined|destroyed\s+my|lost\s+everything/i, w: 2 },
      { re: /امید|فایده.?ای\s*ندارد|هیچ.?وقت\s*خوب\s*نمی|تسلیم|دیگر?\s*نمی.?توانم/, w: 3 },
      { re: /خراب\s*شدم|نابود|همه.?چیز.*از\s*دست/, w: 2 },
    ],
    replies: {
      en: [
        'Tonight is a bad night to decide anything about the rest of your life. Withdrawal lies specifically about the future — that is one of its documented symptoms, not an insight you have arrived at.',
        'You do not have to believe it gets better. You only have to not act on tonight. Those are different, and the second one is achievable.',
        'Whatever you conclude at this hour, hold it until daylight and look at it again. If it is still true at noon, it is worth taking somewhere — to a person, not to me.',
      ],
      fa: [
        'امشب شب بدی است برای تصمیم گرفتن دربارهٔ باقی زندگی‌ات. ترک، دقیقاً دربارهٔ آینده دروغ می‌گوید — این یکی از علائم ثبت‌شده‌اش است، نه بینشی که به آن رسیده‌ای.',
        'لازم نیست باور کنی بهتر می‌شود. فقط لازم است بر اساس امشب عمل نکنی. این دو فرق دارند، و دومی شدنی است.',
        'هر نتیجه‌ای در این ساعت می‌گیری، تا روشنایی نگهش دار و دوباره نگاهش کن. اگر ظهر هم درست بود، ارزش دارد جایی ببری‌اش — پیش یک آدم، نه پیش من.',
      ],
    },
  },
  {
    id: 'angry',
    patterns: [
      { re: /angry|furious|unfair|not\s+fair|hate\s+(this|it|them)|doctor\s+(did|never|should)/i, w: 3 },
      { re: /عصبانی|خشم|بی.?انصافی|ناعادلانه|متنفرم|دکتر.*نگفت/, w: 3 },
    ],
    replies: {
      en: [
        'The anger is reasonable. A lot of people were handed these for a few weeks and never told what stopping would be like. Being angry about that is not a symptom to manage.',
        'Keep it. It is more useful than shame, and it is more accurate. Just do not spend it at 3am on a night you also have to survive.',
        'You are allowed to be furious and still take care of yourself tonight. Those do not cancel each other out.',
      ],
      fa: [
        'این خشم منطقی است. خیلی‌ها این‌ها را برای چند هفته گرفتند و هرگز به‌شان نگفتند قطع کردنش چه شکلی است. عصبانی بودن از این، علامتی نیست که لازم باشد مدیریت شود.',
        'نگهش دار. از شرم مفیدتر است و دقیق‌تر هم هست. فقط ساعت سه بامداد خرجش نکن، شبی که باید از آن هم جان سالم به در ببری.',
        'هم می‌توانی خشمگین باشی و هم امشب مراقب خودت. این دو همدیگر را باطل نمی‌کنند.',
      ],
    },
  },
  {
    id: 'restless',
    patterns: [
      { re: /restless|wired|can'?t\s+(sit|lie|stay)\s+still|racing\s+(thoughts|mind)|mind\s+won'?t\s+stop/i, w: 3 },
      { re: /بی.?قرار|قرار\s*ندارم|فکر.?ها.*نمی.?ایستد|مغزم\s*نمی.?ایستد|ذهنم\s*تند/, w: 3 },
    ],
    replies: {
      en: [
        'A racing mind will not be argued down, but it will follow the body. Give it a rhythm to lock onto — the box pattern is best for this, four equal sides, nothing to think about.',
        'If lying still is unbearable, stop lying still. Walk slowly around the room for two minutes, then get back in. Fighting the bed makes the bed the enemy.',
        'Say one sentence out loud on repeat until it gets boring. Boring is the target — you are trying to under-stimulate a system that is over-stimulated.',
      ],
      fa: [
        'ذهن تندرو را نمی‌شود با استدلال آرام کرد، ولی دنبال بدن می‌آید. به آن ریتمی بده که رویش قفل شود — الگوی مربع برای همین بهترین است، چهار ضلع برابر، بدون چیزی برای فکر کردن.',
        'اگر بی‌حرکت دراز کشیدن تحمل‌ناپذیر است، دراز نکش. دو دقیقه آرام در اتاق راه برو، بعد برگرد. جنگیدن با تخت، تخت را دشمن می‌کند.',
        'یک جمله را بلند تکرار کن تا خسته‌کننده شود. خسته‌کننده شدن هدف است — داری سیستمی را که بیش‌تحریک شده کم‌تحریک می‌کنی.',
      ],
    },
  },
  {
    id: 'whatdo',
    patterns: [
      { re: /what\s+(should|can|do)\s+i\s+do|help\s+me|what\s+now|any\s+ideas/i, w: 2 },
      { re: /چه\s*کار\s*کنم|چیکار\s*کنم|کمکم\s*کن|حالا\s*چی/, w: 2 },
    ],
    replies: {
      en: [
        'Pick the smallest one: breathe out longer than you breathe in, twelve times. That is it. Nothing after that is required of you tonight.',
        'In order — feet on the floor, long exhale, one sentence on repeat, sound on low. If you get through all four and it is still bad, come back and tell me.',
        'Do not aim at sleep. Aim at the next twenty minutes. Open Breath and follow the circle; it removes the decision, which is most of the difficulty.',
      ],
      fa: [
        'کوچک‌ترین کار را انتخاب کن: دوازده بار بازدم را بلندتر از دم کن. همین. بعد از آن امشب هیچ‌چیز از تو خواسته نیست.',
        'به ترتیب — کف پا روی زمین، بازدم بلند، یک جمله در تکرار، صدا با ولوم کم. اگر هر چهارتا را انجام دادی و باز بد بود، برگرد و بگو.',
        'هدف را خواب نگذار. هدف را بیست دقیقهٔ بعدی بگذار. «نفس» را باز کن و دایره را دنبال کن؛ تصمیم را از تو می‌گیرد، که بیشترِ سختی همان است.',
      ],
    },
  },
  {
    id: 'greeting',
    patterns: [
      { re: /^\s*(hi|hey|hello|yo|salam)\b/i, w: 2 },
      // No trailing `\b`: JavaScript's word boundary is ASCII-only, so after a
      // Persian letter it needs an ASCII word character to follow. A plain
      // "سلام" — or "سلام!" — matched nothing, which meant the offline bank
      // never recognised a Persian greeting at all. The offline bank is the
      // default path when no companion endpoint is configured.
      { re: /^\s*(سلام|درود|های)/, w: 2 },
    ],
    replies: {
      en: [
        'I am here. What is happening tonight?',
        'Hello. It is late — what has you awake?',
        'Here. Take your time, there is nothing else scheduled.',
      ],
      fa: [
        'اینجا هستم. امشب چه خبر است؟',
        'سلام. دیروقت است — چه چیزی بیدار نگهت داشته؟',
        'هستم. عجله نکن، برنامهٔ دیگری در کار نیست.',
      ],
    },
  },
  {
    id: 'thanks',
    patterns: [
      { re: /thank|thanks|appreciate|helped/i, w: 2 },
      { re: /ممنون|مرسی|سپاس|کمک\s*کرد/, w: 2 },
    ],
    replies: {
      en: [
        'You did the work. I only kept you company while you did it.',
        'Good. Write the night down before you forget it — that part matters later.',
        'Any time. Same place tomorrow night if you need it.',
      ],
      fa: [
        'کار را تو کردی. من فقط همراهی‌ات کردم.',
        'خوب است. شب را قبل از فراموش کردن بنویس — آن قسمتش بعداً مهم می‌شود.',
        'هر وقت خواستی. فردا شب هم همین‌جا هستم.',
      ],
    },
  },
]

const FALLBACK: Record<Lang, string[]> = {
  en: [
    'I hear you. Say more if you want to — there is no rush and nothing else I am doing.',
    'That sounds heavy. What does it feel like in your body right now?',
    'Keep going. It is easier to carry a thing once it is outside your head.',
    'I am still here. If words are too much, we can just do breathing instead.',
  ],
  fa: [
    'می‌شنوم. اگر خواستی بیشتر بگو — عجله‌ای نیست و کار دیگری هم ندارم.',
    'سنگین به نظر می‌رسد. همین حالا در بدنت چه حسی دارد؟',
    'ادامه بده. یک چیز وقتی از سرت بیرون بیاید راحت‌تر حمل می‌شود.',
    'هنوز اینجا هستم. اگر حرف زدن زیاد است، می‌توانیم به‌جایش فقط نفس بکشیم.',
  ],
}

/** Persian text arrives with Arabic look-alikes and ZWNJ; normalise before matching. */
function normalise(text: string): string {
  return text
    .replace(/[ي]/g, 'ی')
    .replace(/[ك]/g, 'ک')
    .replace(/[أإآ]/g, 'ا')
    .replace(/‌/g, ' ')
    .trim()
}

/** Rotates through variants so the same phrasing does not come back twice running. */
const seen = new Map<string, number>()

export function offlineReply(input: string, lang: Lang): string {
  const text = normalise(input)

  let best: Intent | null = null
  let bestScore = 0
  for (const intent of INTENTS) {
    let score = 0
    for (const { re, w } of intent.patterns) if (re.test(text)) score += w
    if (score > bestScore) {
      bestScore = score
      best = intent
    }
  }

  const pool = best ? best.replies[lang] : FALLBACK[lang]
  const key = best?.id ?? 'fallback'
  const idx = (seen.get(key) ?? Math.floor(Math.random() * pool.length)) % pool.length
  seen.set(key, idx + 1)
  return pool[idx]!
}
